import { Component, Switch, Match, Show, createEffect, on } from "solid-js";
import {
  useCheckoutFlow,
  CheckoutStep,
} from "@/contexts/checkout-flow/checkout-flow.provider";
import Review from "@/components/review/Review";
import { CheckoutHeader } from "@/components/checkout-header/CheckoutHeader";
import { CheckoutBody } from "@/components/checkout-body/CheckoutBody";
import { CheckoutFooter } from "@/components/checkout-footer/CheckoutFooter";
import CampaignSelection from "@/components/campaign-selection/CampaignSelection";
import CampaignSelectionPlaceholder from "@/components/campaign-selection/CampaignSelectionPlaceholder";
import GlobalErrorModal from "@/components/modals/global-error-modal/GlobalErrorModal";
import { OrderConfirmationModal } from "@/components/modals/order-confirmation/OrderConfirmation";
import { useCampaignData } from "@/hooks/useCampaignData";
import { useAppRefresh } from "@/contexts/app-refresh/app-refresh.context";
import { MODAL_EXIT_DELAY_MS } from "@/shared/constants/event.const";
import {
  publishCampaignSelectionScreenView,
  publishFinalReviewScreenView,
  publishCancelCampaignScreenView,
} from "@/services/analytics.service";

interface CheckoutFlowProps {
  shouldFetchData?: boolean;
  showPlaceholder?: boolean;
  hasEligibilityErrors?: boolean;
  isLoadingEligibility?: boolean;
}

const CheckoutFlow: Component<CheckoutFlowProps> = (props) => {
  const {
    currentStep,
    dealerId,
    featureToggles,
    cancelCampaign,
    showOrderConfirmation,
    setShowOrderConfirmation,
    exitFeaturedDealer,
    showGlobalError,
    setShowGlobalError,
  } = useCheckoutFlow();

  const { refreshApp } = useAppRefresh();

  const { tiles, adjustedMaxSelections, alertType, isLoading, hasErrors } =
    useCampaignData({
      shouldFetchData: () => props.shouldFetchData ?? false,
      dealerId,
      featureToggles,
      cancelCampaign,
    });

  const showLoading = () => {
    if (props.showPlaceholder) return false;
    return props.isLoadingEligibility || isLoading();
  };

  const showErrors = () => {
    return props.hasEligibilityErrors || hasErrors() || showGlobalError();
  };

  createEffect(
    on(currentStep, (step) => {
      if (step === CheckoutStep.CAMPAIGN_LENGTH) {
        publishCampaignSelectionScreenView();
      } else if (step === CheckoutStep.REVIEW) {
        publishFinalReviewScreenView();
      } else if (step === CheckoutStep.CANCEL) {
        publishCancelCampaignScreenView();
      }
    }),
  );

  const handleErrorModalExit = () => {
    setShowGlobalError(false);
    setTimeout(() => {
      exitFeaturedDealer();
    }, MODAL_EXIT_DELAY_MS);
  };

  const handleOrderConfirmationClose = () => {
    setShowOrderConfirmation(false);
    setTimeout(() => {
      exitFeaturedDealer();
    }, MODAL_EXIT_DELAY_MS);
  };

  return (
    <>
      <Show when={showErrors()}>
        <GlobalErrorModal
          onRefresh={() => refreshApp()}
          onExit={handleErrorModalExit}
        />
      </Show>
      <Show when={showOrderConfirmation()}>
        <OrderConfirmationModal onClose={handleOrderConfirmationClose} />
      </Show>
      <fd-mfe-container
        class="flex max-w-[1280px] mx-auto flex-col gap-FsSizing2x items-center"
        data-testid="checkout-flow"
      >
        <div class="@xs:px-FsSizing5x">
          <CheckoutHeader title="Featured Dealer" />
          <CheckoutBody alertType={alertType()}>
            <Switch>
              <Match when={currentStep() === CheckoutStep.CAMPAIGN_LENGTH}>
                <Show
                  when={!props.showPlaceholder}
                  fallback={<CampaignSelectionPlaceholder />}
                >
                  <fd-mfe-skeleton-loader
                    prop:active={showLoading()}
                    prop:toggleOnLoad={true}
                  >
                    <CampaignSelection
                      tiles={tiles()}
                      maxSelections={adjustedMaxSelections()}
                    />
                  </fd-mfe-skeleton-loader>
                </Show>
              </Match>
              <Match when={currentStep() === CheckoutStep.REVIEW}>
                <Review showCancelButton={false} />
              </Match>
              <Match when={currentStep() === CheckoutStep.CANCEL}>
                <Review showCancelButton={true} />
              </Match>
            </Switch>
          </CheckoutBody>
        </div>
        <CheckoutFooter />
      </fd-mfe-container>
    </>
  );
};

export default CheckoutFlow;
